/**
 * Pailena · fiches produit et page d'accueil
 * - événements CRO (Shopify.analytics.publish + dataLayer), sans dupliquer les événements Shopify natifs
 *   (vue produit, ajout au panier, début de commande restent envoyés par Shopify / pixels)
 * - état « chargement » du bouton d'achat
 * - barre d'achat collante : nom court du produit
 * - défilement vers les ancres internes
 * - vidéo du mouvement : lecture quand visible, pause hors écran, respect de « mouvement réduit »
 * - accueil : clics suivis via data-pl-track="nom_evenement" (hero_primary_cta, movement_click…), inscription newsletter
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- Suivi CRO ---------- */

const sent = new Set();

/**
 * Événements publiés : variant_selected, sticky_add_to_cart, movement_video_play, faq_open,
 * movement_comparison_click, cross_sell_click, trio_interaction, review_click.
 * @param {string} name
 * @param {Record<string, unknown>} [data]
 * @param {string} [onceKey] - n'envoie l'événement qu'une fois par page pour cette clé
 */
function track(name, data = {}, onceKey) {
  if (onceKey) {
    if (sent.has(onceKey)) return;
    sent.add(onceKey);
  }
  const payload = { template: document.getElementById('pailena-pdp')?.dataset.template || 'unknown', ...data };
  try {
    window.Shopify?.analytics?.publish?.(`pailena_${name}`, payload);
  } catch (error) {
    /* l'analytique ne doit jamais casser la page */
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: `pailena_${name}`, ...payload });
}

document.addEventListener('shopify:product:select', (event) => {
  const payload = /** @type {any} */ (event);
  const options = Array.isArray(payload.selectedOptions) ? payload.selectedOptions : [];
  track('variant_selected', {
    product_handle: payload.product?.handle,
    variant_id: payload.detail?.variantId,
    variant_title: options.map((option) => option?.value ?? option?.name ?? option).join(' / '),
  });
});

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('.sticky-add-to-cart__button')) {
      track('sticky_add_to_cart');
    }

    const tracked = target.closest('[data-pl-track]');
    if (tracked) {
      const href = tracked.getAttribute('href');
      const kind = tracked.getAttribute('data-pl-track');
      if (kind === 'review') track('review_click', { href });
      if (kind === 'trio' || kind === 'bundle') track('trio_interaction', { href, context: 'pdp' });
      if (kind === 'movement') track('movement_comparison_click', { href });
      // Accueil : le nom de l'événement est porté directement par l'attribut
      if (kind && /^[a-z]+(_[a-z]+)+$/.test(kind)) track(kind, { href, label: tracked.getAttribute('data-pl-track-label') || undefined });
    }

    const recommendation = target.closest('product-recommendations a[href*="/products/"], product-recommendations .quick-add__button');
    if (recommendation) {
      track('cross_sell_click', { href: recommendation.getAttribute('href') || recommendation.closest('[data-product-url]')?.getAttribute('data-product-url') });
    }
  },
  { capture: true }
);

document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (details instanceof HTMLDetailsElement && details.open && details.hasAttribute('data-pl-faq')) {
      track('faq_open', { question: details.getAttribute('data-pl-faq') });
    }
  },
  { capture: true }
);

// Inscription newsletter (formulaire client Shopify) : aucune donnée personnelle dans l'événement
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (form instanceof HTMLFormElement && form.querySelector('input[name="contact[email]"]') && form.querySelector('input[name="form_type"][value="customer"]')) {
      track('newsletter_submit', { location: form.closest('footer, .shopify-section-group-footer-group') ? 'footer' : 'page' });
    }
  },
  { capture: true }
);

/* ---------- Bouton d'achat : état de chargement ---------- */

// Horizon n'expose pas d'état « en cours » : on l'ajoute le temps de la requête panier.
/** @param {boolean} loading */
function setLoading(loading) {
  document.querySelectorAll('.product-details .add-to-cart-button').forEach((button) => {
    button.classList.toggle('is-loading', loading);
    if (loading) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  });
}

let loadingTimeout = 0;
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    if (!form.closest('.product-details')) return;
    setLoading(true);
    window.clearTimeout(loadingTimeout);
    loadingTimeout = window.setTimeout(() => setLoading(false), 8000);
  },
  { capture: true }
);

document.addEventListener('shopify:cart:error', () => setLoading(false));

/* ---------- Barre d'achat collante : nom court ---------- */

// Horizon affiche le titre complet (« Bague anti-stress rotative Papillon Charmant »), tronqué sur mobile.
// On y met le nom court déjà calculé par le bloc « Pailena · Titre produit ».
function shortenStickyTitle() {
  const name = document.querySelector('.pl-title__name')?.textContent?.trim();
  const title = document.querySelector('.sticky-add-to-cart__title');
  if (name && title && title.textContent?.trim() !== name) {
    title.setAttribute('title', title.textContent?.trim() || '');
    title.textContent = name;
  }
}

shortenStickyTitle();
document.addEventListener('shopify:product:select', () => window.setTimeout(shortenStickyTitle, 600));

/* ---------- Défilement vers les ancres ---------- */

/** @param {string} hash */
function resolveAnchor(hash) {
  if (hash === '#pailena-reco') {
    return (
      document.getElementById('pailena-reco') ||
      document.querySelector('[id$="__recommendations"]') ||
      document.querySelector('product-recommendations')
    );
  }
  try {
    return document.querySelector(hash);
  } catch (error) {
    return null;
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const link = target.closest('a[data-pl-scroll]');
  if (!link) return;
  const hash = new URL(link.getAttribute('href') || '', window.location.href).hash;
  const destination = hash ? resolveAnchor(hash) : null;
  if (!destination) return;
  event.preventDefault();
  destination.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
  if (hash === '#pailena-motion') {
    const video = destination.querySelector('video[data-pl-video]');
    if (video instanceof HTMLVideoElement) playVideo(video, true);
  }
});

/* ---------- Vidéo du mouvement ---------- */

/**
 * @param {HTMLVideoElement} video
 * @param {boolean} [userInitiated]
 */
function playVideo(video, userInitiated = false) {
  if (reducedMotion.matches && !userInitiated && !video.dataset.userPlayed) return;
  if (userInitiated) video.dataset.userPlayed = 'true';
  video.preload = 'auto';
  const attempt = video.play();
  if (attempt) attempt.catch(() => {});
}

/** @param {HTMLVideoElement} video */
function syncToggle(video) {
  const toggle = video.closest('.pl-motion__figure')?.querySelector('[data-pl-video-toggle]');
  if (!toggle) return;
  const playing = !video.paused;
  toggle.setAttribute('aria-pressed', String(playing));
  toggle.classList.toggle('is-playing', playing);
  const label = toggle.querySelector('span');
  if (label) label.textContent = playing ? label.dataset.labelPause || '' : label.dataset.labelPlay || '';
}

document.querySelectorAll('video[data-pl-video]').forEach((element) => {
  const video = /** @type {HTMLVideoElement} */ (element);

  video.addEventListener('playing', () => {
    track('movement_video_play', { src: video.currentSrc }, 'movement_video_play');
    syncToggle(video);
  });
  video.addEventListener('pause', () => syncToggle(video));

  video.closest('.pl-motion__figure')?.querySelector('[data-pl-video-toggle]')?.addEventListener('click', () => {
    if (video.paused) {
      delete video.dataset.userPaused;
      playVideo(video, true);
    } else {
      video.dataset.userPaused = 'true';
      video.pause();
    }
  });

  if (!('IntersectionObserver' in window)) return;
  new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          if (!video.dataset.userPaused) playVideo(video);
        } else if (!video.paused) {
          video.pause();
        }
      }
    },
    { rootMargin: '0px 0px -15% 0px', threshold: 0.35 }
  ).observe(video);
});

/* ---------- Bouton d'achat : fin du chargement après la mise à jour du panier ---------- */

document.addEventListener('shopify:cart:lines-update', async (event) => {
  // L'événement part avant la requête panier : on attend sa promesse quand elle existe.
  const pending = /** @type {any} */ (event).promise;
  if (pending && typeof pending.then === 'function') {
    try {
      await pending;
    } catch (error) {
      setLoading(false);
      return;
    }
  } else {
    await new Promise((resolve) => window.setTimeout(resolve, 900));
  }
  setLoading(false);
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    setLoading(false);
  }
});

/* ---------- Livraison estimée (jours ouvrés, recalculée à la date du visiteur) ---------- */

// La page peut être servie depuis le cache : on recalcule les dates à partir d'aujourd'hui.
const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** @param {Date} date */
const isoDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

function renderDeliveryEstimates() {
  document.querySelectorAll('[data-pl-delivery]').forEach((element) => {
    if (!(element instanceof HTMLElement)) return;
    const min = Number(element.dataset.min || 6);
    const max = Math.max(min, Number(element.dataset.max || 10));
    const closed = new Set((element.dataset.closed || '').split(',').map((value) => value.trim()).filter(Boolean));

    const cursor = new Date();
    cursor.setHours(12, 0, 0, 0);
    let businessDays = 0;
    let start = null;
    let end = null;
    for (let i = 0; i < 90 && !end; i += 1) {
      cursor.setDate(cursor.getDate() + 1);
      const day = cursor.getDay();
      if (day === 0 || day === 6 || closed.has(isoDate(cursor))) continue;
      businessDays += 1;
      if (businessDays === min) start = new Date(cursor);
      if (businessDays === max) end = new Date(cursor);
    }
    if (!start || !end) return;

    const sameMonth = start.getMonth() === end.getMonth();
    const startLabel = sameMonth ? String(start.getDate()) : `${start.getDate()} ${MONTHS_FR[start.getMonth()]}`;
    const endLabel = `${end.getDate()} ${MONTHS_FR[end.getMonth()]}`;
    const startNode = element.querySelector('[data-pl-delivery-start]');
    const endNode = element.querySelector('[data-pl-delivery-end]');
    if (startNode && startNode.textContent !== startLabel) startNode.textContent = startLabel;
    if (endNode && endNode.textContent !== endLabel) endNode.textContent = endLabel;
  });
}

renderDeliveryEstimates();
// Le bloc d'achat est re-rendu par Horizon au changement de variante
document.addEventListener('shopify:product:select', () => window.setTimeout(renderDeliveryEstimates, 700));
